import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState, Field, PageHeader, StatusBadge } from "@/components/shared/Common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { clp } from "@/lib/format";

export const RUN_STATUS = { borrador: ["pendiente", "Borrador"], revisada: ["info", "Revisada"], aprobada: ["activo", "Aprobada"], cerrada: ["inactivo", "Cerrada"], anulada: ["bloqueado", "Anulada"] };
const NUMS = [["uf", "Valor UF"], ["utm", "Valor UTM"], ["imm", "Ingreso mínimo mensual"], ["tope_afp_uf", "Tope imponible AFP/salud (UF)"],
  ["tope_cesantia_uf", "Tope seguro cesantía (UF)"], ["jornada_semanal_legal", "Jornada semanal legal (h)"]];

const Params = () => {
  const { can } = useAuth();
  const canEdit = can("rrhh.planilla.generar");
  const [p, setP] = useState(null);
  const [afp, setAfp] = useState([]);
  useEffect(() => { api.get("/rrhh/payroll/params").then((r) => { setP(r.data); setAfp(Object.entries(r.data.afp)); }); }, []);
  if (!p) return null;
  const setTax = (i, j, v) => setP({ ...p, tax: p.tax.map((t, k) => (k === i ? t.map((x, m) => (m === j ? v : x)) : t)) });
  const save = async () => {
    const body = { ...p, afp: Object.fromEntries(afp.filter(([k]) => k.trim()).map(([k, v]) => [k.trim(), Number(v) || 0])),
      tax: p.tax.map((t, i) => [i === p.tax.length - 1 ? null : Number(t[0]), Number(t[1]), Number(t[2])]) };
    NUMS.forEach(([k]) => { body[k] = Number(body[k]); });
    try { const { data } = await api.put("/rrhh/payroll/params", body); setP(data); toast.success("Parámetros guardados"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="syc-card p-6 space-y-6" data-testid="params-panel">
      <p className="text-xs text-[#5b6656]">Actualice estos valores cada mes según los indicadores de Previred y el SII. Cada pre-nómina guarda los valores usados al generarla o recalcularla.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Field label="Referencia"><Input data-testid="param-ref-input" disabled={!canEdit} value={p.ref} onChange={(e) => setP({ ...p, ref: e.target.value })} /></Field>
        {NUMS.map(([k, l]) => <Field key={k} label={l}><Input data-testid={`param-${k}-input`} type="number" step="any" disabled={!canEdit} value={p[k]} onChange={(e) => setP({ ...p, [k]: e.target.value })} /></Field>)}
      </div>
      <div>
        <div className="font-semibold text-sm mb-2">AFP · tasa total trabajador (10% + comisión)</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">{afp.map(([k, v], i) => (
          <div key={i} className="flex gap-2" data-testid={`param-afp-row-${i}`}>
            <Input disabled={!canEdit} value={k} onChange={(e) => setAfp(afp.map((x, j) => (j === i ? [e.target.value, x[1]] : x)))} />
            <Input className="w-24" type="number" step="any" disabled={!canEdit} value={v} onChange={(e) => setAfp(afp.map((x, j) => (j === i ? [x[0], e.target.value] : x)))} />
            {canEdit && <button onClick={() => setAfp(afp.filter((_, j) => j !== i))} className="text-[#8a9584] hover:text-[#b42318]"><Trash2 size={15} /></button>}
          </div>))}</div>
        {canEdit && <Button data-testid="param-afp-add-btn" variant="outline" size="sm" className="rounded-full mt-2" onClick={() => setAfp([...afp, ["", 10]])}><Plus size={14} className="mr-1" />AFP</Button>}
      </div>
      <div>
        <div className="font-semibold text-sm mb-2">Impuesto único de segunda categoría (tramos en UTM)</div>
        <div className="overflow-x-auto"><table className="text-sm" data-testid="param-tax-table"><thead className="text-xs text-[#5b6656]"><tr><th className="text-left pr-2">Hasta (UTM)</th><th className="text-left pr-2">Factor %</th><th className="text-left">Rebaja (UTM)</th></tr></thead>
          <tbody>{p.tax.map((t, i) => (
            <tr key={i}>{t.map((x, j) => <td key={j} className="pr-2 py-1">{i === p.tax.length - 1 && j === 0 ? <span className="text-xs text-[#5b6656]">y más</span> :
              <Input className="w-28" type="number" step="any" disabled={!canEdit} value={x ?? ""} onChange={(e) => setTax(i, j, e.target.value)} />}</td>)}</tr>))}</tbody></table></div>
      </div>
      {canEdit && <Button data-testid="params-save-btn" className="rounded-full" onClick={save}>Guardar parámetros</Button>}
    </div>
  );
};

const Runs = () => {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  useEffect(() => { api.get("/rrhh/payroll/runs").then((r) => setRuns(r.data)); }, []);
  const generate = async () => {
    try { const { data } = await api.post("/rrhh/payroll/runs", { period }); toast.success(`${data.code} generada`); navigate(`/rrhh/pre-nomina/${data.id}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="syc-card overflow-hidden" data-testid="runs-panel">
      {can("rrhh.planilla.generar") && <div className="flex flex-wrap items-center gap-3 p-4 border-b border-[#e4e8e1]">
        <Input data-testid="run-period-input" type="month" className="w-44" value={period} onChange={(e) => setPeriod(e.target.value)} />
        <Button data-testid="run-generate-btn" className="rounded-full" onClick={generate}><Plus size={15} className="mr-1" />Generar pre-nómina</Button>
        <span className="text-xs text-[#5b6656]">Requiere el período de asistencia cerrado.</span>
      </div>}
      {runs.length === 0 ? <EmptyState title="Sin pre-nóminas" /> : runs.map((r) => (
        <button key={r.id} data-testid={`run-row-${r.id}`} onClick={() => navigate(`/rrhh/pre-nomina/${r.id}`)} className="w-full flex flex-wrap items-center gap-3 px-5 py-3 border-b border-[#f0f2ee] hover:bg-[#f6fbf2] text-sm text-left">
          <span className="font-mono text-xs w-28">{r.code}</span><b className="flex-1">{r.period}</b>
          <span className="text-xs text-[#5b6656]">{r.totals.workers} trabajadores</span><b>{clp(r.totals.liquido ?? r.totals.total)}</b>
          <StatusBadge status={RUN_STATUS[r.status][0]} label={RUN_STATUS[r.status][1]} />
        </button>))}
    </div>
  );
};

export default function Payroll() {
  return (
    <div data-testid="payroll-page">
      <PageHeader eyebrow="RR.HH. · Remuneraciones" title="Pre-nómina" subtitle="Planilla mensual calculada desde la asistencia y las marcas de entrada/salida: sueldo, horas extra, gratificación, AFP, salud, cesantía e impuesto. Cálculo interno de referencia." />
      <Tabs defaultValue="runs">
        <TabsList className="mb-5"><TabsTrigger value="runs" data-testid="payroll-tab-runs">Períodos</TabsTrigger><TabsTrigger value="params" data-testid="payroll-tab-params">Parámetros legales</TabsTrigger></TabsList>
        <TabsContent value="runs"><Runs /></TabsContent>
        <TabsContent value="params"><Params /></TabsContent>
      </Tabs>
    </div>
  );
}
